import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { createSystemRole, updateSystemRole } from '../../../../modules/superadmin';
import { selectCreateRoleLoading, selectUpdateRoleLoading } from '../../../../modules/superadmin/selectors';
import { SystemRole } from '../../../../modules/superadmin/types';

interface Props {
  open: boolean;
  onClose: () => void;
  role: SystemRole | null;
}

const SystemRoleModal: React.FC<Props> = ({ open, onClose, role }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isCreating = useSelector(selectCreateRoleLoading);
  const isUpdating = useSelector(selectUpdateRoleLoading);
  const isSaving = isCreating || isUpdating;

  const [formData, setFormData] = useState({
    role_name: '',
    role_description: ''
  });

  useEffect(() => {
    if (role) {
      setFormData({
        role_name: role.role_name,
        role_description: role.role_description
      });
    } else {
      setFormData({
        role_name: '',
        role_description: ''
      });
    }
  }, [role, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    if (role) {
      dispatch(updateSystemRole({ role_id: role.role_id, ...formData }));
    } else {
      dispatch(createSystemRole(formData));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {role ? 'Edit System Role' : 'Create System Role'}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
          <TextField
            label="Role Name"
            name="role_name"
            value={formData.role_name}
            onChange={handleChange}
            fullWidth
            required
            size="small"
          />
          <TextField
            label="Description"
            name="role_description"
            value={formData.role_description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#666' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || !formData.role_name}
          sx={{
            textTransform: 'none',
            backgroundColor: '#5E8E7E',
            '&:hover': { backgroundColor: '#4A7A6A' }
          }}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemRoleModal;
